import React, { useEffect, useState } from "react";
import { FaAngleUp } from "react-icons/fa6";
import { Tooltip } from "react-tooltip";
import { ChangingLanguage } from "../ChangingLanguage";
import { useAppSelector } from "../../hook";
import { useTranslation } from "react-i18next";
import { normalizedLocale } from "../../../common/libs/normalizedLocale";
import { getProductGroupDisplayName } from "../../../common/libs/productGroupLocale";
import { ChangingProductId } from "../ChangingProductId";
import Cookies from "js-cookie";
import {
  addOrganisationProductGroups,
  generateProductGroupDocs,
  getOrganisationProductGroups,
  getProductGroups,
} from "../../../common/api/authApi";
import ModalComponent from "../../Ui/ModalComponent/ModalWindow";
import ModalButton from "../../Ui/Buttons/ModalButtons";
import ProductGroupSignModal, {
  ProductGroupSignFile,
} from "../../Ui/ProductGroupSignModal/ProductGroupSignModal";
import { useLocation } from "react-router-dom";
import style from "./index.module.css";
import AlertBox from "../../Ui/AlertBox/AlertBox";
import Loader from "../../Ui/Loader/Loader";
// @ts-ignore
import { NCALayerClient } from "ncalayer-js-client";

type OrganisationProductGroup = {
  id: number;
  organizationId: number;
  productGroupId: number;
  productGroupName: string;
  productGroupCode: string;
};
function Header() {
  const [open, setOpen] = useState(false);
  const userData: any = useAppSelector((state) => state.auth.userData);
  const { t, i18n } = useTranslation();
  const locale = normalizedLocale(i18n.language);

  const [activeGroup, setActiveGroup] = useState<OrganisationProductGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState<any>(null);
  const [groups, setGroups] = useState<any[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [checked, setChecked] = useState<boolean[]>([]);
  const [saving, setSaving] = useState(false);
  const [signFiles, setSignFiles] = useState<ProductGroupSignFile[] | null>(null);
  const [signSubmitting, setSignSubmitting] = useState(false);

  const location = useLocation();

  const toggleByIndex = (idx: number) => {
    setChecked((prev) => {
      const next = prev.map((v, i) => (i === idx ? !v : v));

      const nextSelected = groups
        .filter((_, i) => next[i])
        .map((g) => g.id as number);

      setSelectedIds(nextSelected);

      return next;
    });
  };

  const closeSignModal = () => {
    setSignFiles(null);
  };

  const refreshActiveGroups = async () => {
    const data = await getOrganisationProductGroups();
    setActiveGroup(data);
    if (data?.length) {
      setModalOpen(false);
      closeSignModal();

      const fromCookie = Number(Cookies.get("activeGroup"));
      const valid =
        Number.isFinite(fromCookie) &&
        data.some((g: any) => g.productGroupId === fromCookie);
      const selectedProductGroupId = valid
        ? fromCookie
        : data[0]?.productGroupId;
      setSelectedGroupId(selectedProductGroupId);
      Cookies.set("activeGroup", String(selectedProductGroupId), {
        expires: 180,
        path: "/",
      });
      const selectedGroup = data.find(
        (g: any) => g.productGroupId === selectedProductGroupId,
      );
      if (selectedGroup?.productGroupCode) {
        Cookies.set("activeGroupCode", String(selectedGroup.productGroupCode), {
          expires: 180,
          path: "/",
        });
      }
    }
    return data;
  };

  const handleAcceptGroups = async () => {
    if (!selectedIds.length) {
      setAlertOpen({
        status: "error",
        message: "Выберите товарную группу",
      });
      return;
    }

    try {
      setSaving(true);
      const response = await generateProductGroupDocs(selectedIds);
      const files = Array.isArray(response) ? response : response?.data ?? [];
      if (!files.length) {
        setAlertOpen({
          status: "error",
          message: t("productGroupSign.generateError"),
        });
        return;
      }
      setSignFiles(files);
    } catch (e: any) {
      setAlertOpen({
        status: "error",
        message:
          e?.response?.data?.message || t("productGroupSign.generateError"),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSignAndConnect = async () => {
    if (!signFiles?.length || !selectedIds.length) return;
    setSignSubmitting(true);

    const ncalayerClient = new NCALayerClient();
    try {
      await ncalayerClient.connect();
    } catch (error) {
      setAlertOpen({
        status: "error",
        message: t("productGroupSign.ncalayerError"),
      });
      setSignSubmitting(false);
      return;
    }

    const cmsData = signFiles
      .map((file) => file?.sha256Hash)
      .filter(Boolean)
      .join(",");

    let signature: any;
    try {
      signature = await ncalayerClient.basicsSignCMS(
        NCALayerClient.basicsStoragesAll,
        cmsData,
        NCALayerClient.basicsCMSParams,
        process.env.REACT_APP_ETRACK_TEST === "true"
          ? NCALayerClient.basicsSignerTestAny
          : NCALayerClient.basicsSignerAny,
        "ru",
      );
    } catch (error) {
      console.error("Ошибка при выполнении CMS-подписи:", error);
      setSignSubmitting(false);
      return;
    }

    if (Array.isArray(signature)) {
      signature = signature[0];
    }
    signature = signature
      .replace("-----BEGIN CMS-----", "")
      .replace("-----END CMS-----", "")
      .trim();

    try {
      await addOrganisationProductGroups(selectedIds, signature);
      await refreshActiveGroups();
    } catch (error: any) {
      setAlertOpen({
        status: "error",
        message:
          error?.response?.data?.message || t("productGroupSign.connectError"),
      });
    } finally {
      setSignSubmitting(false);
    }
  };

  useEffect(() => {
    Promise.all([getProductGroups(), getOrganisationProductGroups()])
      .then(([allGroups, activeGroups]) => {
        setActiveGroup(activeGroups);

        if (!Array.isArray(activeGroups) || activeGroups.length === 0) {
          setModalOpen(true);
        }

        setGroups(allGroups);

        const nextChecked = allGroups.map((pg: any) =>
          activeGroups.some((ag: any) => ag.productGroupId === pg.id),
        );
        setChecked(nextChecked);

        setSelectedIds(
          allGroups
            .filter((_: any, idx: number) => nextChecked[idx])
            .map((pg: any) => pg.id as number),
        );

        const fromCookie = Number(Cookies.get("activeGroup"));

        const valid =
          Number.isFinite(fromCookie) &&
          activeGroups.some((g: any) => g.productGroupId === fromCookie);

        const selectedProductGroupId = valid
          ? fromCookie
          : activeGroups[0]?.productGroupId;

        setSelectedGroupId(selectedProductGroupId);

        Cookies.set("activeGroup", String(selectedProductGroupId), { expires: 180, path: "/" });
      })
      .catch((e) => {
        console.error("Header onLoad: не удалось загрузить группы", e);
        setModalOpen(true);
      });
  }, [location.pathname]);

  return (
    <>
      {saving && <Loader />}
      <header className="flex flex-row items-center grow-0 flex-none h-16 pt-5 pb-5 w-full justify-end">
        <div className="flex flex-col px-3">
          <span className="text-gray-600 font-semibold">
            {userData.userName
              ? `${userData.userName} ${userData.userSurname}`
              : "Тест Тестович"}
          </span>

          <span className="text-green py-1">
            {locale == "ru" ? `${userData.userPositionRu}` : `${userData.userPositionKz}`}
            {/* {userData.userPosition ? `${userData.userPositionRu}` : "-"} */}
          </span>
        </div>
        <div>
          <button
            onClick={() => setOpen(!open)}
            className="flex flex-row items-center"
            data-tooltip-id="user-profile"
          >
            <div className="relative flex justify-center items-center align-middle mx-auto object-cover rounded-full h-12 w-12 bg-white text-gray-400">
              <span className="text-gray-600">
                {" "}
                {userData.userName ? `${userData.userName[0]}` : "Т"}
              </span>
            </div>
            <span
              className={`cursor-pointer px-2  text-gray-600 ${!open && "rotate-180"}`}
            >
              <FaAngleUp />
            </span>
          </button>
          <Tooltip
            id="user-profile"
            className="bg-white rounded duration-200 mt-2"
            style={{
              backgroundColor: "rgb(255, 255, 255)",
              color: "#222",
              margin: "0",
              padding: "0",
              zIndex: "100",
            }}
            openOnClick={true}
            clickable={true}
            noArrow={true}
            opacity={1}
            place={"bottom-end"}
            afterShow={() => setOpen(true)}
            afterHide={() => setOpen(false)}
          >
            <ul id={`dropdown-example-user-profile`}>

              <li
                className="flex rounded-md p-2 cursor-pointer hover:bg-light-white items-center gap-x-4
              hover:bg-green hover:text-white"
              >
                <a className="origin-left duration-200"
                  href="/profile"
                >
                  {t("submenu.profile")}
                </a>
              </li>

            </ul>
          </Tooltip>
        </div>
        {/* <img
        src="/icons/bell.svg"
        className="mx-2 duration-200 group-hover:brightness-200 group-hover:invert-1"
      /> */}
        <div className="bg-white p-2 ml-2 rounded ">
          <ChangingProductId onlyDesktop={true} activeGroup={activeGroup} selectedGroupId={selectedGroupId} setSelectedGroupId={setSelectedGroupId} />
        </div>
        <div className="bg-white p-2 ml-2 rounded ">
          <ChangingLanguage onlyDesktop={true} />
        </div>
      </header>
      {!activeGroup.length && modalOpen && (
        <ModalComponent
          classNameModal={style.modalWrapper2}
          classNameContent="max-w-[1585px] w-full m-t-[20px]"
          isOpen={!activeGroup.length && modalOpen}
          onClose={() => {
            getOrganisationProductGroups().then((data) => {
              setActiveGroup(data);
              data.length ? setModalOpen(false) : setAlertOpen({
                status: "error",
                message: "Выберите торговую группу",
              });
            });
          }}
          headerContent={<h1 className="h__modal">
            {"Выберите товарную группу"}
          </h1>}
          content={
            <div className="w-full mx-auto flex justify-between mt-[25px]">
              <div className="flex w-full justify-between gap-4">
                {groups.map((group, idx) => {
                  const isOn = checked[idx] || false;
                  const trackCls = isOn
                    ? "relative inline-block w-11 h-6 flex-shrink-0 rounded-full bg-green transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-5 after:h-5 after:rounded-full after:bg-white after:transition-transform after:translate-x-5"
                    : "relative inline-block w-11 h-6 flex-shrink-0 rounded-full bg-gray-200 transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-5 after:h-5 after:rounded-full after:bg-white after:transition-transform";

                  return (
                    <label
                      key={group.id}
                      className="relative flex items-center w-full cursor-pointer select-none rounded-xl border border-gray-200 shadow-md p-4 bg-white transition-colors"
                    >
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={isOn}
                        onChange={() => toggleByIndex(idx)}
                        disabled={saving}
                      />
                      <span className={trackCls} />
                      <span className="relative ml-3 text-sm font-medium text-gray-900 flex-1">
                        {getProductGroupDisplayName(group, locale)}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          }
          customContent={
            <ModalButton
              color="green"
              type="button"
              disabled={saving}
              onClick={handleAcceptGroups}
            >
              {t("mainButtons.accept")}
            </ModalButton>
          }
        />
      )}
      <ProductGroupSignModal
        isOpen={!!signFiles?.length}
        files={signFiles}
        productGroups={groups}
        signing={signSubmitting}
        onClose={closeSignModal}
        onSign={handleSignAndConnect}
      />
      <AlertBox isOpen={alertOpen} setIsOpen={setAlertOpen} />
    </>
  );
}

Header.propTypes = {};

export default Header;
